import { CampaignOfferAttention, CampaignType } from 'api/graphql-global-types';
import { GetCampaignOffers_getCampaignOffers_entities } from 'api/campaignV2/types/GetCampaignOffers';

// Campaign version constant
const CampaignVersion = {
  V3: '3',
};

/**
 * Check if campaign supports counter offers
 * Counter offers are available for V3 campaigns OR non-Social campaigns
 */
export const hasCounterOffer = (
  offer?: GetCampaignOffers_getCampaignOffers_entities | null
): boolean => {
  if (!offer) return false;

  const isCampaignV3 = offer.campaign?.v === CampaignVersion.V3;
  const isSocial = offer.campaign?.type === CampaignType.Social;

  return isCampaignV3 || (!isCampaignV3 && !isSocial);
};

/**
 * Check if it's the athlete's turn to respond to the counter offer
 */
export const isAthletesTurn = (
  offer?: GetCampaignOffers_getCampaignOffers_entities | null
): boolean => {
  return (
    hasCounterOffer(offer) &&
    offer?.counterOfferAttention === CampaignOfferAttention.TALENT
  );
};

/**
 * Check if it's the brand's turn to respond to the counter offer
 */
export const isBrandsTurn = (
  offer?: GetCampaignOffers_getCampaignOffers_entities | null
): boolean => {
  return (
    hasCounterOffer(offer) &&
    offer?.counterOfferAttention === CampaignOfferAttention.BRAND
  );
};

/**
 * Calculate original amount from offer
 */
export const getOriginalAmounts = (
  offer?: GetCampaignOffers_getCampaignOffers_entities | null
): { originalTalentFee: number; originalBrandFee: number } => {
  if (!offer) return { originalTalentFee: 0, originalBrandFee: 0 };
  return {
    originalTalentFee:
      offer.originalInfluencerFee ?? offer.estimatedInfluencerFee ?? 0,
    originalBrandFee: offer.originalTotalFee ?? offer.estimatedTotalFee ?? 0,
  };
};

export type CounterOfferPair = {
  counterOffer: NonNullable<
    GetCampaignOffers_getCampaignOffers_entities['counterOffers']
  >[number];
  response:
    | NonNullable<
        GetCampaignOffers_getCampaignOffers_entities['counterOffers']
      >[number]
    | null;
  counterNumber: number;
};

/**
 * Groups counter offers into pairs (counter offer + response).
 * Backend sorts by createdAt descending (most recent first).
 * We reverse to get chronological order (oldest first), then pair them.
 * Each counter offer shows the next one (chronologically newer) as its response.
 * If a counter offer doesn't have a response, it was sent by athlete (athlete always initiates),
 * so we still show it.
 * We don't show items twice (if used as response, don't show as counter).
 *
 * @param counterOffers - Array of counter offers (sorted descending by createdAt)
 */
export const groupCounterOffersIntoPairs = (
  counterOffers: NonNullable<
    GetCampaignOffers_getCampaignOffers_entities['counterOffers']
  >
): CounterOfferPair[] => {
  if (!counterOffers || counterOffers.length === 0) {
    return [];
  }

  // Reverse to get chronological order (oldest first)
  const chronologicalOffers = [...counterOffers].reverse();

  const pairs: CounterOfferPair[] = [];
  const usedAsResponse = new Set<string>();
  let counterNumber = 1;

  // Pair each counter offer with the next one (chronologically newer) as its response
  for (let i = 0; i < chronologicalOffers.length; i++) {
    const counterOffer = chronologicalOffers[i];
    const response =
      i + 1 < chronologicalOffers.length ? chronologicalOffers[i + 1] : null;

    // Skip if this counter offer was already used as a response
    if (usedAsResponse.has(counterOffer.id)) {
      continue;
    }

    // If there's a response, mark it as used
    if (response) {
      usedAsResponse.add(response.id);
    }

    // Always add pairs (if no response, it's athlete-initiated so we show it)
    pairs.push({
      counterOffer,
      response,
      counterNumber: counterNumber++,
    });
  }

  // Reverse to get newest first, as brand should show the latest counter offer first
  return pairs.reverse();
};
